import React, { useState, useEffect, useCallback } from 'react';
import ProductFields from './ProductFields';
import ScoreSlider from './ScoreSlider';
import OfficialBadge from './OfficialBadge';
import AttachmentPicker from './AttachmentPicker';
import { useI18n } from './i18n';
import {
  PRODUCT_INITIAL, productPayload, COFFEE_SCORED, COFFEE_CUPS, ROAST_LEVELS, coffeeResult,
} from './productScoring';

const DRAFT_KEY = 'tnh2_draft_coffee';

const INITIAL = {
  ...PRODUCT_INITIAL,
  sample_code: '', roast_level: '',
  tasting_date: new Date().toISOString().slice(0, 10), venue: '', taster: '',
  ...Object.fromEntries(COFFEE_SCORED.map((k) => [k, null])),
  uniformity_cups: 5, clean_cup_cups: 5, sweetness_cups: 5, taint_cups: 0, fault_cups: 0,
  notes: '', visibility: 'private',
};

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...INITIAL, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return INITIAL;
}

// Form state from an API sheet: nulls keep the defaults, product fields come from the nested product
function fromSheet(sheet) {
  const out = { ...INITIAL };
  const product = sheet.product || {};
  out.product_name = sheet.product_name || product.name || '';
  ['producer', 'origin', 'variety'].forEach((k) => { out[k] = sheet[k] || product[k] || ''; });
  out.harvest_year = sheet.harvest_year || product.harvest_year || '';
  Object.keys(INITIAL).forEach((k) => {
    if (k in PRODUCT_INITIAL) return;
    if (sheet[k] !== undefined && sheet[k] !== null) out[k] = sheet[k];
  });
  return out;
}

const CUP_OPTIONS = [0, 1, 2, 3, 4, 5];

function CupCounter({ name, value, onChange, label, hint }) {
  return (
    <div className="counter-row">
      <label htmlFor={`coffee-${name}`}>{label}{hint && <small className="import-hint"> {hint}</small>}</label>
      <select id={`coffee-${name}`} value={value} onChange={(e) => onChange(name, parseInt(e.target.value, 10))}>
        {CUP_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

// SCA 2004 cupping form (legacy). Props: onSubmit(data, attachmentItems), initialData (a sheet to edit), onCancel.
function CoffeeForm({ onSubmit, initialData, onCancel }) {
  const { t, tc, formatNumber } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [attachmentItems, setAttachmentItems] = useState([]);

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const patch = useCallback((changes) => setForm((prev) => ({ ...prev, ...changes })), []);
  const handleInput = (e) => patch({ [e.target.name]: e.target.value });
  const setValue = useCallback((name, value) => patch({ [name]: value }), [patch]);

  const result = coffeeResult(form);

  const handleReset = () => {
    if (!window.confirm(t('coffee.confirm_reset'))) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) { alert(t('coffee.alert_name')); return; }
    const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    const data = {
      ...productPayload(form),
      sample_code: text(form.sample_code), roast_level: form.roast_level || null,
      tasting_date: form.tasting_date || null, venue: text(form.venue), taster: text(form.taster), notes: text(form.notes),
      visibility: form.visibility,
      ...Object.fromEntries(COFFEE_SCORED.map((k) => [k, form[k]])),
      ...Object.fromEntries(COFFEE_CUPS.concat(['taint_cups', 'fault_cups']).map((k) => [k, form[k]])),
    };
    onSubmit(data, attachmentItems);
    if (editing) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      <OfficialBadge status="formal" source={t('coffee.title')} note={t('coffee.badge_note')} sourceNote={t('products.source_note.coffee')} />

      <fieldset>
        <legend>{t('coffee.sec.product')}</legend>
        <div className="form-grid">
          <ProductFields category="coffee" values={form} onChange={patch} />
          <div className="form-field">
            <label>{t('coffee.f.sample_code')}</label>
            <input name="sample_code" value={form.sample_code} onChange={handleInput} maxLength={50} />
          </div>
          <div className="form-field">
            <label>{t('coffee.f.roast_level')}</label>
            <select name="roast_level" value={form.roast_level} onChange={handleInput}>
              <option value="">—</option>
              {ROAST_LEVELS.map((r) => <option key={r} value={r}>{tc('products.roast', r)}</option>)}
            </select>
            <small className="import-hint">{t('coffee.roast_note')}</small>
          </div>
          <div className="form-field">
            <label>{t('coffee.f.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('coffee.f.venue')}</label>
            <input name="venue" value={form.venue} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('coffee.f.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInput} />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>{t('coffee.sec.attributes')}</legend>
        <small className="import-hint">{t('coffee.scale_hint')}</small>
        {COFFEE_SCORED.map((name) => (
          <ScoreSlider key={name} label={t(`coffee.p.${name}`)} hint={t(`coffee.def.${name}`)} value={form[name]}
            min={6} max={9.75} step={0.25} onChange={(v) => setValue(name, v)} />
        ))}
      </fieldset>

      <fieldset>
        <legend>{t('coffee.sec.cups')}</legend>
        <small className="import-hint">{t('coffee.cups_hint')}</small>
        {COFFEE_CUPS.map((name) => (
          <CupCounter key={name} name={name} value={form[name]} onChange={setValue}
            label={t(`coffee.p.${name}`)} hint={t(`coffee.def.${name}`)} />
        ))}
      </fieldset>

      <fieldset>
        <legend>{t('coffee.sec.defects')}</legend>
        <small className="import-hint">{t('coffee.defects_hint')}</small>
        <CupCounter name="taint_cups" value={form.taint_cups} onChange={setValue}
          label={t('coffee.p.taint_cups')} hint={t('coffee.def.taint_cups')} />
        <CupCounter name="fault_cups" value={form.fault_cups} onChange={setValue}
          label={t('coffee.p.fault_cups')} hint={t('coffee.def.fault_cups')} />
      </fieldset>

      <fieldset>
        <legend>{t('coffee.sec.result')}</legend>
        <div className="live-result">
          <span className="live-total">
            {result.total !== null ? `${formatNumber(result.total, 2)}/100` : '—'}
          </span>
          {result.qualityClass && <span className={`pairing-badge-sm ${result.qualityClass.replace(/_/g, '-')}`}>{tc('coffee.class', result.qualityClass)}</span>}
          <span>{t('coffee.res.attributes')}: <strong>{formatNumber(result.attributes, 2)}</strong></span>
          <span>{t('coffee.res.cups')}: <strong>+{result.cups}</strong></span>
          <span>{t('coffee.res.defects')}: <strong>−{result.defects}</strong></span>
          {result.total === null && <small>{t('coffee.res.incomplete', { n: result.scoredCount })}</small>}
        </div>
        <div className="form-field">
          <label>{t('coffee.f.notes')}</label>
          <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('coffee.ph.notes')} />
        </div>
      </fieldset>

      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => setValue('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('coffee.save_edit') : t('coffee.save_new')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default CoffeeForm;
